import { isFuture, isPast } from 'date-fns';
import { AccountResource } from '../../resources';
import {
  DefaultExchangePeriodHours,
  ExchangePeriodHoursType,
  SourceCurrencyList,
  TargetCurrencyList,
} from '../../types';
import {
  getDateTimeInHoursAndMinuteAtBudapestTZ,
  getDateTimeInHoursAtBudapestTZ,
  isArrayEqual,
  isDefined,
  Loadable,
  LoadableState,
  NbLogger,
} from '../../utils';

type SourceCurrencyListReloadState = {
  force: boolean;
  lastLoadedHour: number | undefined;
  morningChecked: boolean;
  afternoonChecked: boolean;
  nightChecked: boolean;
};

export type CurrencyExchangeSourceAndTargetCurrencyManagerMessages = {
  loadTargetCurrenciesFailed: string;
  loadSourceCurrenciesFailed: string;
};

export type CurrencyExchangeSourceAndTargetCurrencyManagerSettersGetters = {
  get activeSourceCurrencies(): LoadableState<string[]>;

  set activeSourceCurrencies(value: LoadableState<string[]>);

  get sourceCurrencies(): LoadableState<SourceCurrencyList>;

  set sourceCurrencies(value: LoadableState<SourceCurrencyList>);

  get targetCurrencies(): LoadableState<TargetCurrencyList>;

  set targetCurrencies(value: LoadableState<TargetCurrencyList>);
};

export type CurrencyExchangeSourceAndTargetCurrencyManagerParams = {
  getSourceCurrencies: (targetCurrency?: string) => ReturnType<AccountResource['getSourceCurrencies']>;
  getTargetCurrencies: (currency: string) => ReturnType<AccountResource['getTargetCurrencies']>;
  messages: CurrencyExchangeSourceAndTargetCurrencyManagerMessages;
  settersGetters: CurrencyExchangeSourceAndTargetCurrencyManagerSettersGetters;
  exchangePeriodHours?: ExchangePeriodHoursType;
  logger: NbLogger;
};

export class CurrencyExchangeSourceAndTargetCurrencyManager {
  static getInstance(
    params: CurrencyExchangeSourceAndTargetCurrencyManagerParams,
  ): CurrencyExchangeSourceAndTargetCurrencyManager {
    return new CurrencyExchangeSourceAndTargetCurrencyManager(params);
  }

  // These fields and methods are react specific, so need to be updated whenever their reference or value changes
  shouldLoadSourceCurrencies: boolean | undefined = undefined;
  isCashInfoLoaded: boolean | undefined = undefined;
  showError: ((msg: string) => void) | undefined;

  private readonly exchangePeriodHours: ExchangePeriodHoursType;
  private readonly messages: CurrencyExchangeSourceAndTargetCurrencyManagerMessages;
  private readonly logger: NbLogger;

  private refreshCurrenciesTimer: ReturnType<typeof setInterval> | null = null;
  private isSourceCurrenciesPending = false;
  private getSourceCurrencies: (targetCurrency?: string) => ReturnType<AccountResource['getSourceCurrencies']>;
  private getTargetCurrencies: (currency: string) => ReturnType<AccountResource['getTargetCurrencies']>;
  private settersGetters: CurrencyExchangeSourceAndTargetCurrencyManagerSettersGetters;
  private targetCurrencyForFilteredSourceCurrencies?: string;

  private sourceCurrencyReloadState: SourceCurrencyListReloadState = {
    force: false,
    lastLoadedHour: undefined,
    morningChecked: false,
    afternoonChecked: false,
    nightChecked: false,
  };

  private constructor({
    getSourceCurrencies,
    getTargetCurrencies,
    messages,
    settersGetters,
    exchangePeriodHours,
    logger,
  }: CurrencyExchangeSourceAndTargetCurrencyManagerParams) {
    this.getSourceCurrencies = getSourceCurrencies;
    this.getTargetCurrencies = getTargetCurrencies;
    this.messages = messages;
    this.settersGetters = settersGetters;
    this.exchangePeriodHours = exchangePeriodHours ?? DefaultExchangePeriodHours;
    this.logger = logger;

    const [nowH, nowM] = getDateTimeInHoursAndMinuteAtBudapestTZ(new Date());
    this.sourceCurrencyReloadState.morningChecked =
      nowH >= this.exchangePeriodHours.morning[0] && nowM >= this.exchangePeriodHours.morning[1];
    this.sourceCurrencyReloadState.afternoonChecked =
      nowH >= this.exchangePeriodHours.afternoon[0] && nowM >= this.exchangePeriodHours.afternoon[1];
    this.sourceCurrencyReloadState.nightChecked =
      nowH >= this.exchangePeriodHours.night[0] && nowM >= this.exchangePeriodHours.night[1];
  }

  updateRefs(
    getSourceCurrencies: (targetCurrency?: string) => ReturnType<AccountResource['getSourceCurrencies']>,
    getTargetCurrencies: (currency: string) => ReturnType<AccountResource['getTargetCurrencies']>,
  ) {
    this.getSourceCurrencies = getSourceCurrencies;
    this.getTargetCurrencies = getTargetCurrencies;
  }

  startRefreshCurrencies() {
    this.logger.debug(`Starting refresh currencies timer`);
    this.refreshCurrenciesTimer = setInterval(this.refreshCurrencies.bind(this), 5000);
  }

  stopRefreshCurrencies() {
    if (this.refreshCurrenciesTimer) {
      clearInterval(this.refreshCurrenciesTimer);
      this.refreshCurrenciesTimer = null;
    }
  }

  resetLoadables(): void {
    this.stopRefreshCurrencies();
    this.settersGetters.sourceCurrencies = Loadable.uninitialized;
    this.settersGetters.activeSourceCurrencies = Loadable.uninitialized;

    try {
      this.settersGetters.targetCurrencies = Loadable.uninitialized;
    } catch {
      // Some flows, such as order exchange, do not use target currencies.
    }
  }

  async loadTargetCurrencies(currency: string): Promise<TargetCurrencyList | null> {
    try {
      this.settersGetters.targetCurrencies = Loadable.loading;
      const result = await this.getTargetCurrencies(currency);
      const targetCurrencyList = result.data as TargetCurrencyList;
      this.settersGetters.targetCurrencies = Loadable.loaded(targetCurrencyList);
      return targetCurrencyList;
    } catch (err) {
      this.logger.error('Error while loading target currencies', err);
      this.showError?.(this.messages.loadTargetCurrenciesFailed);
      this.settersGetters.targetCurrencies = Loadable.failed;
      return null;
    }
  }

  private refreshCurrencies() {
    this.calcActiveSourceCurrencies(this.settersGetters.sourceCurrencies);
    if (this.isReloadable(this.settersGetters.sourceCurrencies)) {
      this.loadSourceCurrencies({ force: true, targetCurrency: this.targetCurrencyForFilteredSourceCurrencies });
    } else {
      // Erste has fixed exchangePeriods, so we can predict when the next period starts, based on Hungary time
      // These are: 8-17-22
      // TODO NN-3315 this is a temporary solution, until we don't have exact exchangePeriod times
      if (
        (!this.sourceCurrencyReloadState.morningChecked ||
          !this.sourceCurrencyReloadState.afternoonChecked ||
          !this.sourceCurrencyReloadState.nightChecked) &&
        this.sourceCurrencyReloadState.lastLoadedHour
      ) {
        const [currentHourAtBp, currentMinAtBp] = getDateTimeInHoursAndMinuteAtBudapestTZ(new Date());
        if (
          !this.sourceCurrencyReloadState.nightChecked &&
          currentHourAtBp >= this.exchangePeriodHours.night[0] &&
          currentMinAtBp >= this.exchangePeriodHours.night[1]
        ) {
          this.sourceCurrencyReloadState.nightChecked = true;
          this.sourceCurrencyReloadState.afternoonChecked = true;
          this.sourceCurrencyReloadState.morningChecked = true;
          this.loadSourceCurrencies({ force: true, targetCurrency: this.targetCurrencyForFilteredSourceCurrencies });
        } else if (
          !this.sourceCurrencyReloadState.afternoonChecked &&
          currentHourAtBp >= this.exchangePeriodHours.afternoon[0] &&
          currentMinAtBp >= this.exchangePeriodHours.afternoon[1]
        ) {
          this.sourceCurrencyReloadState.morningChecked = true;
          this.sourceCurrencyReloadState.afternoonChecked = true;
          this.loadSourceCurrencies({ force: true, targetCurrency: this.targetCurrencyForFilteredSourceCurrencies });
        } else if (
          !this.sourceCurrencyReloadState.morningChecked &&
          currentHourAtBp >= this.exchangePeriodHours.morning[0] &&
          currentMinAtBp >= this.exchangePeriodHours.morning[1]
        ) {
          this.sourceCurrencyReloadState.morningChecked = true;
          this.loadSourceCurrencies({ force: true, targetCurrency: this.targetCurrencyForFilteredSourceCurrencies });
        }
      }
    }
  }

  private calcActiveSourceCurrencies(sourceCurrencies: LoadableState<SourceCurrencyList>) {
    if (sourceCurrencies.isLoading) {
      return;
    } else if (sourceCurrencies.isLoaded) {
      const newActiveSourceCurrencies = sourceCurrencies
        .filter(
          sourceCurrency =>
            isDefined(sourceCurrency.exchangePeriodFrom) &&
            isDefined(sourceCurrency.exchangePeriodTo) &&
            isPast(new Date(sourceCurrency.exchangePeriodFrom)) &&
            isFuture(new Date(sourceCurrency.exchangePeriodTo)),
        )
        .map(sourceCurrency => sourceCurrency.currency!);

      if (
        !this.settersGetters.activeSourceCurrencies.isLoaded ||
        (this.settersGetters.activeSourceCurrencies.isLoaded &&
          !isArrayEqual(newActiveSourceCurrencies, this.settersGetters.activeSourceCurrencies)) ||
        this.sourceCurrencyReloadState.force
      ) {
        this.sourceCurrencyReloadState.force = false;
        this.settersGetters.activeSourceCurrencies = Loadable.loaded(newActiveSourceCurrencies);
      }
    }
  }

  private isReloadable(sourceCurrencies: LoadableState<SourceCurrencyList>) {
    if (!this.shouldLoadSourceCurrencies || sourceCurrencies.isLoading || sourceCurrencies.hasError) {
      return false;
    } else if (sourceCurrencies.isLoaded) {
      return sourceCurrencies.some(
        sourceCurrency =>
          (isDefined(sourceCurrency.exchangePeriodFrom) &&
            isDefined(sourceCurrency.exchangePeriodTo) &&
            isPast(new Date(sourceCurrency.exchangePeriodFrom)) &&
            isPast(new Date(sourceCurrency.exchangePeriodTo))) ||
          // TODO NN-3417 remove this optimization, since backend will handle cases when there is no exchangePeriodFrom, but could be
          (isDefined(sourceCurrency.exchangePeriodFrom) &&
            sourceCurrency.exchangePeriodTo === null &&
            isPast(new Date(sourceCurrency.exchangePeriodFrom))),
      );
    } else {
      return sourceCurrencies.hasError;
    }
  }

  async loadSourceCurrencies(props?: {
    force?: boolean;
    targetCurrency?: string;
  }): Promise<LoadableState<string[]> | null> {
    this.logger.debug(`Loading source currencies, force=${props?.force}, target=${props?.targetCurrency}`);
    const isCurrencyChanged = this.targetCurrencyForFilteredSourceCurrencies !== props?.targetCurrency;
    this.targetCurrencyForFilteredSourceCurrencies = props?.targetCurrency;
    try {
      if (
        this.isSourceCurrenciesPending ||
        !this.shouldLoadSourceCurrencies ||
        (this.settersGetters.sourceCurrencies.isLoaded && !props?.force && !isCurrencyChanged) ||
        !this.isCashInfoLoaded
      ) {
        return null;
      }

      this.isSourceCurrenciesPending = true;
      this.sourceCurrencyReloadState.force = !!props?.force;
      this.settersGetters.sourceCurrencies = Loadable.loading;
      this.calcActiveSourceCurrencies(this.settersGetters.sourceCurrencies);
      const result = await this.getSourceCurrencies(this.targetCurrencyForFilteredSourceCurrencies);

      // TODO NN-3417 remove this optimization, since backend will handle cases when there is no exchangePeriodFrom, but could be.
      // Explanation:
      // When a currency is not convertible, the backend marks this by setting the exchangePeriodFrom to null. But in some cases
      // we know that this currency is convertible, but not in the current period. We know when the next period starts. This hack
      // focuses on finding an active currency and taking it's exchangePeriodTo as the exchangePeriodFrom of the currency with null value
      // This is a temporary solution, until the backend is able to handle this case.
      // Technically the UI will check these timestamps, and if the current time is after the exchangePeriodTo we refresh the source
      // currencies hence the source currencies will always be consistent and up to date.
      // This is only working when there is at least one currency with exchangePeriodTo set
      const currencyWithExchangePeriodTo = result.data.find(_ => !!_.exchangePeriodTo);
      if (currencyWithExchangePeriodTo) {
        result.data.forEach(currency => {
          currency.exchangePeriodFrom = currency.exchangePeriodFrom || currencyWithExchangePeriodTo?.exchangePeriodTo;
        });
      }

      this.sourceCurrencyReloadState.lastLoadedHour = getDateTimeInHoursAtBudapestTZ(new Date());
      this.settersGetters.sourceCurrencies = Loadable.loaded(result.data as SourceCurrencyList);
      this.calcActiveSourceCurrencies(this.settersGetters.sourceCurrencies);
    } catch (e) {
      this.logger.error('Error while loading source currencies', e);
      this.showError?.(this.messages.loadSourceCurrenciesFailed);
      this.settersGetters.sourceCurrencies = Loadable.failed;
      this.calcActiveSourceCurrencies(this.settersGetters.sourceCurrencies);
    } finally {
      this.isSourceCurrenciesPending = false;
    }
    return this.settersGetters.activeSourceCurrencies;
  }
}
